import { useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { CheckCircle2, Mail, Loader2 } from "lucide-react";
import { useSeo } from "../components/site/Layout";
import { Section } from "../components/site/blocks";
import { Field, inputProps, Honeypot, Consent, CONSENT_MSG } from "../components/site/FormField";
import { api, formatApiError, EMAIL_RE, PHONE_RE, CP_RE } from "../lib/api";
import { COMPANY, SERVICES } from "../data/site";
import { useSiteConfig } from "../components/site/SiteConfig";

const NEEDS = [...SERVICES.map((s) => s.title), "Je ne sais pas encore / autre"];
const STRUCTURES = ["bailleur social", "collectivité", "établissement médico-social", "copropriété/syndic", "autre"];
const SUCCESS = "Merci, votre demande a bien été envoyée. Nous vous recontactons sous 48 h ouvrées.";
const STEP_OF = { organisation: 0, structure_type: 0, need: 0, name: 1, email: 1, phone: 1, postal_code: 1, message: 2, consent: 2 };
const STEP_LABELS = ["Votre demande", "Vos coordonnées", "Votre message"];

const validate = (f) => {
  const e = {};
  if (!f.name.trim() || f.name.trim().length < 2) e.name = "Merci d'indiquer votre nom.";
  if (!f.email.trim()) e.email = "Merci d'indiquer votre email.";
  else if (!EMAIL_RE.test(f.email.trim())) e.email = "Adresse email invalide (exemple : nom@domaine.fr).";
  if (!f.phone.trim()) e.phone = "Merci d'indiquer un numéro de téléphone.";
  else if (!PHONE_RE.test(f.phone.trim())) e.phone = "Numéro invalide (exemple : 06 12 34 56 78).";
  if (!CP_RE.test(f.postal_code.trim())) e.postal_code = "Code postal à 5 chiffres requis.";
  if (!f.need) e.need = "Merci de préciser votre besoin.";
  if (f.message.trim().length < 10) e.message = "Merci de décrire votre projet en quelques mots (10 caractères minimum).";
  if (f.profile === "professionnel") {
    if (!f.organisation.trim()) e.organisation = "Merci d'indiquer votre organisation.";
    if (!f.structure_type) e.structure_type = "Merci de choisir un type de structure.";
  }
  if (!f.consent) e.consent = CONSENT_MSG;
  return e;
};

const Toggle = ({ name, value, options, onChange, label }) => (
  <fieldset>
    <legend className="font-semibold text-navy mb-2">{label}</legend>
    <div className="grid grid-cols-2 gap-2 rounded-2xl bg-mist p-1.5">
      {options.map(([v, l]) => (
        <label key={v} className={`cursor-pointer rounded-xl px-4 py-3 text-center font-semibold transition-colors ${value === v ? "bg-navy text-white shadow" : "text-navy hover:bg-white"}`}>
          <input type="radio" name={name} value={v} checked={value === v} onChange={() => onChange(v)} className="sr-only" data-testid={`toggle-${name}-${v}`} />
          {l}
        </label>
      ))}
    </div>
  </fieldset>
);

export const DevisForm = () => {
  const [params] = useSearchParams();
  const [f, setF] = useState({
    request_type: params.get("type") === "rappel" ? "rappel" : "devis",
    profile: params.get("profil") === "professionnel" ? "professionnel" : "particulier",
    name: "", email: "", phone: "", postal_code: "", need: NEEDS.includes(params.get("besoin")) ? params.get("besoin") : "",
    message: "", organisation: "", structure_type: "", consent: false, website: "",
  });
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState("idle");
  const [step, setStep] = useState(0);
  const [serverError, setServerError] = useState("");
  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));

  const submit = async (ev) => {
    ev.preventDefault();
    const all = validate(f);
    const stepErr = Object.fromEntries(Object.entries(all).filter(([k]) => STEP_OF[k] === step));
    setServerError("");
    if (step < 2) {
      setErrors(stepErr);
      if (Object.keys(stepErr).length) return document.getElementById(Object.keys(stepErr)[0])?.focus();
      return setStep(step + 1);
    }
    const e = all;
    setErrors(e);
    if (Object.keys(e).length) {
      const first = Object.keys(e)[0];
      setStep(STEP_OF[first]);
      setTimeout(() => document.getElementById(first)?.focus(), 30);
      return;
    }
    setStatus("loading");
    try {
      await api.post("/leads", { ...f, email: f.email.trim(), phone: f.phone.trim(), postal_code: f.postal_code.trim() });
      setStatus("done");
    } catch (err) {
      setServerError(formatApiError(err.response?.data?.detail));
      setStatus("idle");
    }
  };

  if (status === "done") return (
    <div role="status" data-testid="devis-success-message" className="rounded-[28px] bg-mist p-8 sm:p-12 border border-navy/10">
      <CheckCircle2 className="h-12 w-12 text-green-700" aria-hidden="true" />
      <p className="mt-5 font-display text-2xl sm:text-3xl text-navy">{SUCCESS}</p>
      <p className="mt-4 text-slate-700">Un accusé de réception vous est envoyé par email. Vous pouvez aussi préparer votre rendez-vous diagnostic.</p>
      <div className="mt-8 flex flex-wrap gap-3">
        <Link to="/prendre-rdv" className="btn-accent">Prendre RDV</Link>
        <Link to="/ressources" className="btn-outline">Lire nos guides</Link>
      </div>
    </div>
  );

  return (
    <form noValidate onSubmit={submit} data-testid="devis-contact-form" className="relative rounded-[28px] border border-brand/15 bg-white p-6 sm:p-10 shadow-[0_30px_60px_-40px_rgba(16,36,62,0.45)] space-y-6">
      <div aria-label={`Étape ${step + 1} sur 3`}>
        <div className="flex items-center gap-2">
          {[0, 1, 2].map((i) => <span key={i} className={`h-1.5 flex-1 rounded-full transition-colors ${i <= step ? "bg-brand-ink" : "bg-brand-100"}`} />)}
        </div>
        <p className="mt-3 flex justify-between text-sm"><span className="font-display text-navy">{STEP_LABELS[step]}</span><span className="text-slate-600" data-testid="devis-step-indicator">Étape {step + 1} / 3</span></p>
      </div>
      {step === 0 && <>
      <div className="grid sm:grid-cols-2 gap-6">
        <Toggle name="request_type" label="Votre demande" value={f.request_type} onChange={(v) => setF((p) => ({ ...p, request_type: v }))} options={[["devis", "Un devis"], ["rappel", "Être rappelé"]]} />
        <Toggle name="profile" label="Vous êtes" value={f.profile} onChange={(v) => setF((p) => ({ ...p, profile: v }))} options={[["particulier", "Particulier"], ["professionnel", "Professionnel"]]} />
      </div>
      {f.profile === "professionnel" && (
        <div className="grid sm:grid-cols-2 gap-6 rounded-2xl bg-mist-50 p-5 border border-navy/10 rise" data-testid="pro-fields">
          <Field id="organisation" label="Organisation" required error={errors.organisation}>
            <input {...inputProps("organisation", errors)} value={f.organisation} onChange={set("organisation")} autoComplete="organization" />
          </Field>
          <Field id="structure_type" label="Type de structure" required error={errors.structure_type}>
            <select {...inputProps("structure_type", errors)} value={f.structure_type} onChange={set("structure_type")}>
              <option value="">Choisir…</option>
              {STRUCTURES.map((s) => <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>)}
            </select>
          </Field>
        </div>
      )}
      <Field id="need" label="Votre besoin" required error={errors.need}>
        <select {...inputProps("need", errors)} value={f.need} onChange={set("need")}>
          <option value="">Choisir…</option>
          {NEEDS.map((n) => <option key={n} value={n}>{n}</option>)}
        </select>
      </Field>
      </>}
      {step === 1 && (
      <div className="grid sm:grid-cols-2 gap-6">
        <Field id="name" label="Nom et prénom" required error={errors.name}>
          <input {...inputProps("name", errors)} value={f.name} onChange={set("name")} autoComplete="name" />
        </Field>
        <Field id="email" label="Email" required error={errors.email}>
          <input {...inputProps("email", errors)} type="email" value={f.email} onChange={set("email")} autoComplete="email" />
        </Field>
        <Field id="phone" label="Téléphone" required error={errors.phone}>
          <input {...inputProps("phone", errors)} type="tel" value={f.phone} onChange={set("phone")} autoComplete="tel" placeholder="06 12 34 56 78" />
        </Field>
        <Field id="postal_code" label="Code postal du logement" required error={errors.postal_code}>
          <input {...inputProps("postal_code", errors)} inputMode="numeric" maxLength={5} value={f.postal_code} onChange={set("postal_code")} autoComplete="postal-code" />
        </Field>
      </div>
      )}
      {step === 2 && <>
      <Field id="message" label="Votre message" required error={errors.message} hint="Situation, type de logement, délais souhaités…">
        <textarea {...inputProps("message", errors)} rows={5} value={f.message} onChange={set("message")} />
      </Field>
      <Honeypot value={f.website} onChange={set("website")} />
      <Consent checked={f.consent} onChange={set("consent")} error={errors.consent} />
      </>}
      {serverError && <p role="alert" data-testid="devis-server-error" className="rounded-xl bg-red-50 text-red-800 p-4">{serverError}</p>}
      <div className="flex flex-wrap gap-3 justify-between">
        <button type="button" className="btn-outline" onClick={() => { setErrors({}); setStep(step - 1); }} disabled={step === 0} data-testid="devis-prev">Précédent</button>
        {step < 2 ? (
          <button type="submit" className="btn-accent px-8" data-testid="devis-next">Suivant</button>
        ) : (
          <button type="submit" className="btn-accent px-8" disabled={status === "loading"} data-testid="devis-submit-button">
            {status === "loading" && <Loader2 className="h-5 w-5 animate-spin" aria-hidden="true" />} Envoyer ma demande
          </button>
        )}
      </div>
      <p className="text-sm text-slate-600">* Champs obligatoires.</p>
    </form>
  );
};

export default function Contact() {
  useSeo("Demander un devis ou être rappelé", "Demandez un devis ou un rappel à Habitalys pour votre projet d'adaptation, de sécurisation ou de rénovation énergétique. Réponse sous 48 h ouvrées.");
  const { phone } = useSiteConfig();
  return (
    <Section className="bg-mist" testid="contact-page">
      <div className="grid lg:grid-cols-12 gap-12">
        <div className="lg:col-span-4">
          <p className="eyebrow">Contact & devis</p>
          <h1 className="mt-4 text-4xl sm:text-5xl leading-tight">Parlons de votre projet</h1>
          <p className="mt-5 text-lg text-slate-600 font-light">Trois étapes rapides. Réponse sous 48 h ouvrées.</p>
          <div className="mt-8 space-y-4 text-slate-700">
            <p className="flex gap-3"><Mail className="h-5 w-5 text-brand-ink mt-1" aria-hidden="true" /><a className="link-u text-navy font-semibold" href={`mailto:${COMPANY.email}`}>{COMPANY.email}</a></p>
            {phone && <p data-testid="contact-phone">Téléphone : <a className="link-u text-navy font-semibold" href={`tel:${phone.replace(/\s/g, "")}`}>{phone}</a></p>}
            <p>{COMPANY.street}, {COMPANY.city}</p>
          </div>
          <div className="mt-10 rounded-2xl bg-navy text-mist p-6">
            <p className="font-display text-xl">Vous préférez fixer un rendez-vous ?</p>
            <Link to="/prendre-rdv" className="btn-accent mt-4" data-testid="contact-rdv-link">Choisir un créneau</Link>
          </div>
        </div>
        <div className="lg:col-span-8"><DevisForm /></div>
      </div>
    </Section>
  );
}
